import * as React from 'react';

import styled, { css } from 'styled-components';

import {
  PaymentRequest,
  PaymentRequestOptions,
  PaymentMethod,
} from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';

import Flex from '@components/layout/flex';
import PaymentIcon from '@components/basics/icons/payment';

interface PaymentRequestButtonProps {
  className?: string;
  paymentRequestOptions?: PaymentRequestOptions;
  onPaymentMethod: (paymentMethod: PaymentMethod, complete: Function) => void;
}

export const PaymentRequestButton: React.FC<PaymentRequestButtonProps> = ({
  className,
  paymentRequestOptions,
  onPaymentMethod,
}) => {
  const [paymentRequest, setPaymentRequest] = React.useState<
    PaymentRequest | undefined
  >();
  const [isApplePay, setIsApplePay] = React.useState(false);

  const stripe = useStripe();

  React.useEffect(() => {
    if (stripe && paymentRequestOptions) {
      const pr = stripe.paymentRequest(paymentRequestOptions);

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setIsApplePay(result.applePay);
        }
      });

      pr.on('paymentmethod', (ev) => {
        onPaymentMethod(ev.paymentMethod, ev.complete);
      });
    }
  }, [stripe, paymentRequestOptions, onPaymentMethod]);

  return paymentRequest ? (
    <PaymentButton
      className={className}
      onClick={() => paymentRequest.show()}
      alignItems="center"
      justifyContent="center"
      isApplePay={isApplePay}
    >
      <PaymentIcon company={isApplePay ? 'apple' : 'google'} />
    </PaymentButton>
  ) : null;
};

export default PaymentRequestButton;

const PaymentButton = styled(Flex)<{ isApplePay: boolean }>`
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  width: 100%;
  height: 45px;

  & > svg {
    height: 24px;
  }

  ${({ isApplePay }) =>
    isApplePay
      ? css`
          border: 1px solid ${({ theme }) => theme.colors.black};
          transition: background-color
            ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
        `
      : css`
          box-shadow:${({ theme }) =>
            `${theme.general.boxShadowInput} ${theme.colors.shadow}`};
      }
          transition: background-color ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
            box-shadow ${({ theme }) =>
              `${theme.transitions.duration} ${theme.transitions.type}`};
        `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.header};

    ${({ isApplePay }) =>
      !isApplePay &&
      css`
        box-shadow: ${({ theme }) =>
          `${theme.general.boxShadowInputHover} ${theme.colors.shadow}`};
    };
      `}
  }
`;
